import { useCallback, useEffect, useRef, useState } from 'react'
import { checkBdApp } from '../bdapp/useBdApp'
import { publish } from '../bdapp/useBdAppEventBus'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { SalesReportRequest } from './types'

export type SalesReportExportFormat = 'csv' | 'xlsx'

export type SalesReportExportState =
  | { status: 'idle' }
  | { status: 'running', format: SalesReportExportFormat }
  | { status: 'error', format: SalesReportExportFormat, message: string }

export const EXPORT_POLL_INTERVAL_MS = 2000
export const EXPORT_MAX_WAIT_MS = 7 * 60_000

const MESSAGES: Record<string, string> = {
  EXPORT_IN_PROGRESS: 'Já existe uma exportação em andamento. Aguarde ela terminar.',
  EXPORT_TOO_LARGE: 'O relatório é grande demais para exportar. Refine os filtros.',
  REPORT_TOO_LARGE: 'O relatório é grande demais. Refine os filtros.',
  EXPORT_TIMEOUT: 'A exportação demorou demais. Tente de novo.',
  EXPORT_NOT_FOUND: 'A exportação expirou. Tente de novo.',
  CODE_INVALID: 'Código de confirmação inválido ou expirado. Tente de novo.',
  HASH_2FA_ARE_NOT_SETUP: 'Configure a verificação em duas etapas para exportar os contatos.',
  TERMS_NOT_ACCEPTED: 'É preciso aceitar o termo para exportar os contatos.',
}
const DEFAULT_MESSAGE = 'Não foi possível exportar o relatório.'

export const exportErrorMessage = (code: unknown): string =>
  typeof code === 'string' && MESSAGES[code] !== undefined ? MESSAGES[code] : DEFAULT_MESSAGE

const errorCodeOf = (err: any): unknown =>
  err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' ? 'EXPORT_TIMEOUT' : err?.response?.data?.errorCode ?? err?.message

export const downloadFromUrl = (url: string, fileName: string, mimeType: string): void => {
  if (checkBdApp().isNative) {
    publish('download-from-url', { url, fileName, mimeType })
    return
  }
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const wait = async (ms: number): Promise<void> => await new Promise((resolve) => setTimeout(resolve, ms))

export interface UseSalesReportExport<R> {
  state: SalesReportExportState
  start: (format: SalesReportExportFormat, request: R) => Promise<void>
}

export const useSalesReportExport = <R extends object = SalesReportRequest>(
  eventId: number,
  endpoint: string = 'salesReportExport'
): UseSalesReportExport<R> => {
  const [state, setState] = useState<SalesReportExportState>({ status: 'idle' })
  const controller = useRef<AbortController | null>(null)

  useEffect(
    () => () => {
      controller.current?.abort()
      controller.current = null
    },
    []
  )

  const start = useCallback(
    async (format: SalesReportExportFormat, request: R): Promise<void> => {
      controller.current?.abort()
      const current = new AbortController()
      controller.current = current
      const active = (): boolean => controller.current === current && !current.signal.aborted
      const deadline = Date.now() + EXPORT_MAX_WAIT_MS
      const remaining = (): number => {
        const left = deadline - Date.now()
        if (left <= 0) throw new Error('EXPORT_TIMEOUT')
        return left
      }

      setState({ status: 'running', format })
      const api = APIDecoratorWithBaseURI()
      const base = `producer/analytics/${endpoint}?eventId=${eventId}`
      try {
        const started = await api.post(base, { ...request, format }, { signal: current.signal, timeout: remaining() })
        if (!active()) return
        const jobId: string = started.data.body.jobId
        for (;;) {
          await wait(Math.min(EXPORT_POLL_INTERVAL_MS, remaining()))
          if (!active()) return
          const polled = await api.get(`${base}&jobId=${encodeURIComponent(jobId)}`, {
            signal: current.signal,
            timeout: remaining(),
          })
          if (!active()) return
          const body = polled.data.body
          if (body.status === 'done') {
            downloadFromUrl(body.url, body.fileName, body.mimeType)
            setState({ status: 'idle' })
            return
          }
          if (body.status === 'error') throw new Error(body.error)
        }
      } catch (err) {
        if (active()) setState({ status: 'error', format, message: exportErrorMessage(errorCodeOf(err)) })
      } finally {
        if (controller.current === current) controller.current = null
      }
    },
    [eventId, endpoint]
  )

  return { state, start }
}
